import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Check,
  Crown,
  Sparkles,
  TrendingUp,
  Zap,
  Gift,
  ArrowRight,
  Star,
  Shield,
  Users,
  Utensils,
  ChefHat,
  Rocket
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import Logo from '@/components/Logo';
import { usePlans, getFeatureDisplayText } from '@/hooks/usePlans';
import { stripeApi } from '@/services/api/stripeApi';
import { salesApi } from '@/services/api/salesApi';
import { apiClient } from '@/services/api/apiClient';
import { getApiUrl } from '@/config/api';
import Cookies from 'js-cookie';

interface Plan {
  id: string;
  name: string;
  displayName: string;
  price: number;
  originalPrice?: number;
  period: string;
  description: string;
  popular?: boolean;
  features: string[];
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  gradient: string;
  buttonText: string;
  savings?: string;
  // Virtual Host specific fields
  minutesIncluded?: number;
  callsEstimate?: number;
  highlight?: string;
}

const PlanSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const { data: plansData, loading, error } = usePlans();

  // Activation flow detection (sales portal pre-onboarding)
  // Check both keys: plan_selection_session_token (from OAuth callback) and activation_session_token (direct from ActivationLanding)
  const planSelectionToken = localStorage.getItem('plan_selection_session_token');
  const activationToken = localStorage.getItem('activation_session_token');
  const rawSessionToken = planSelectionToken || activationToken;

  console.log('🔍 [PlanSelection] LocalStorage check:', {
    plan_selection_session_token: planSelectionToken,
    activation_session_token: activationToken,
    rawSessionToken: rawSessionToken,
    type: typeof rawSessionToken
  });

  // Handle case where localStorage returns the string "null" instead of null
  const sessionToken = rawSessionToken === 'null' || !rawSessionToken ? undefined : rawSessionToken;
  const isActivationFlow = !!sessionToken;

  console.log('🎯 [PlanSelection] Final sessionToken:', {
    sessionToken: sessionToken,
    isActivationFlow: isActivationFlow
  });

  // Get state from navigation (passed from ProductSelection/OAuthCallback)
  const hasExtractedMenu = location.state?.hasExtractedMenu;
  const itemsExtractedFromState = location.state?.itemsExtracted;
  const fromOAuth = location.state?.fromOAuth;
  const prefillData = location.state?.prefillData;
  const isOnboarding = location.state?.onboarding || searchParams.get('onboarding') === 'true';
  const productTypeFromState = location.state?.productType;

  // Get product type selection (from ProductSelection page or localStorage)
  const productType = productTypeFromState || localStorage.getItem('selected_product_type') || 'virtual_assistant';

  // Get the auth provider from URL params (passed from login)
  const authProvider = searchParams.get('provider') || 'google';
  const userName = searchParams.get('name') || 'there';
  const isUpgradeMode = searchParams.get('mode') === 'upgrade';
  const isUpsellMode = searchParams.get('mode') === 'upsell';
  const upsellProduct = searchParams.get('product'); // virtual_host or virtual_assistant
  const targetPlan = searchParams.get('targetPlan'); // New: auto-select this plan
  const isFrictionless = searchParams.get('frictionless') === 'true';
  const itemsExtracted = itemsExtractedFromState || searchParams.get('items') || '0';

  // For upsell mode, get the restaurant account from localStorage
  const upsellRestAccountId = isUpsellMode ? localStorage.getItem('upsell_rest_account_id') : null;
  const upsellRestaurantName = isUpsellMode ? localStorage.getItem('upsell_restaurant_name') : null;

  // DEBUG: Log when component mounts and any state changes
  console.log('🎯 PlanSelection mounted', {
    selectedPlan,
    isProcessing,
    authProvider,
    userName,
    targetPlan,
    isOnboarding,
    hasExtractedMenu,
    itemsExtracted
  });

  // Fetch current subscription if in upgrade mode
  useEffect(() => {
    if (isUpgradeMode) {
      fetchCurrentSubscription();
      // Default to quarterly for upgrades (no monthly option)
      setBillingPeriod('quarterly');
    }
  }, [isUpgradeMode]);

  // Auto-select target plan if specified
  useEffect(() => {
    if (targetPlan && !selectedPlan) {
      setSelectedPlan(targetPlan);
      // Scroll to the plan selection area after a short delay
      setTimeout(() => {
        const element = document.getElementById('plan-selection');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [targetPlan, selectedPlan]);

  // Reset billing period for Virtual Host (no quarterly option)
  useEffect(() => {
    if (productType === 'virtual_host' && billingPeriod === 'quarterly') {
      setBillingPeriod('monthly');
    }
  }, [productType, billingPeriod]);

  // Safety net: Redirect to product selection if onboarding and no product selected
  useEffect(() => {
    const hasProductType = productTypeFromState || localStorage.getItem('selected_product_type');

    // Only redirect if this is an onboarding flow, not an upgrade, not upsell, and no product selected
    if (isOnboarding && !hasProductType && !isUpgradeMode && !isUpsellMode) {
      console.log('⚠️ [PlanSelection] No product selected, redirecting to product selection');
      navigate('/product-selection', {
        state: {
          fromOAuth: true,
          onboarding: true,
          hasExtractedMenu,
          itemsExtracted: itemsExtractedFromState,
          prefillData
        }
      });
    }
  }, [isOnboarding, productTypeFromState, isUpgradeMode, isUpsellMode, navigate, hasExtractedMenu, itemsExtractedFromState, prefillData]);

  const fetchCurrentSubscription = async () => {
    try {
      const response = await apiClient.get('/subscription/current');
      setCurrentSubscription(response.data);
      // For upgrades, don't use monthly - default to quarterly or annual
      if (isUpgradeMode) {
        setBillingPeriod(response.data.billing_cycle === 'annual' ? 'annual' : 'quarterly');
      } else {
        setBillingPeriod(response.data.billing_cycle || 'monthly');
      }
    } catch (error) {
      console.error('Failed to fetch current subscription:', error);
      toast({
        title: "Error",
        description: "Failed to load current subscription",
        variant: "destructive"
      });
    }
  };

  // Filter plans based on mode and product type
  const getFilteredPlans = () => {
    if (!plansData) return [];

    let filteredPlans = plansData.plans;

    // Filter by product type (Virtual Assistant vs Virtual Host)
    if (productType === 'virtual_host') {
      // Show Virtual Host plans: basic_virtual_host, standard_virtual_host, premium_virtual_host
      filteredPlans = filteredPlans.filter(plan =>
        plan.name.includes('virtual_host')
      );
    } else {
      // Show Virtual Assistant plans: yummy, delicious, gourmet
      filteredPlans = filteredPlans.filter(plan =>
        !plan.name.includes('virtual_host')
      );
    }

    // In upgrade mode, only show plans higher than current
    if (isUpgradeMode && currentSubscription) {
      const planOrder = { yummy: 1, delicious: 2, gourmet: 3 };
      const currentPlanOrder = planOrder[currentSubscription.plan_name as keyof typeof planOrder] || 0;
      filteredPlans = filteredPlans.filter(plan =>
        planOrder[plan.name as keyof typeof planOrder] > currentPlanOrder
      );
    }

    return filteredPlans;
  };

  // Virtual Host plan specific data (matching VirtualHost.tsx)
  const virtualHostPlanData: Record<string, { minutes: number; calls: number; highlight: string; description: string; displayName: string; monthlyPrice: number; annualPrice: number }> = {
    starter_virtual_host: { minutes: 1000, calls: 200, highlight: 'Email support', description: 'Perfect for getting started', displayName: 'Starter', monthlyPrice: 149, annualPrice: 1609 },
    basic_virtual_host: { minutes: 2000, calls: 400, highlight: 'Email support', description: 'For small restaurants', displayName: 'Basic', monthlyPrice: 250, annualPrice: 2700 },
    standard_virtual_host: { minutes: 2750, calls: 550, highlight: 'Custom voice · Priority support', description: 'Most popular choice', displayName: 'Standard', monthlyPrice: 350, annualPrice: 3780 },
    premium_virtual_host: { minutes: 5000, calls: 1000, highlight: 'Advanced voice · Phone support', description: 'For busy restaurants', displayName: 'Premium', monthlyPrice: 600, annualPrice: 6480 },
  };

  const plans: Plan[] = getFilteredPlans().map(plan => ({
    id: plan.name,
    name: plan.name,
    // Use short display name for Virtual Host plans (e.g., "Starter" not "Starter Virtual Host")
    displayName: virtualHostPlanData[plan.name]?.displayName || plan.displayName,
    price: plan.pricing.monthly.price, // Always the monthly price
    originalPrice: plan.pricing.monthly.price, // Same as price for consistency
    period: billingPeriod === 'monthly' ? 'month' : billingPeriod === 'quarterly' ? 'quarter' : 'year',
    // Use short description for Virtual Host plans
    description: virtualHostPlanData[plan.name]?.description || plan.description,
    popular: plan.name === 'delicious' || plan.name === 'standard_virtual_host',
    features: getFeatureDisplayText(plan),
    icon: plan.name === 'yummy' ? Utensils :
          plan.name === 'delicious' ? ChefHat :
          plan.name === 'starter_virtual_host' ? Zap :
          plan.name === 'basic_virtual_host' ? Rocket :
          plan.name === 'standard_virtual_host' ? Sparkles :
          Crown,
    gradient: plan.name === 'yummy' ? 'from-green-400 to-green-600' :
              plan.name === 'delicious' ? 'from-blue-400 to-blue-600' :
              plan.name === 'starter_virtual_host' ? 'from-emerald-400 to-emerald-600' :
              plan.name === 'basic_virtual_host' ? 'from-blue-400 to-blue-600' :
              plan.name === 'standard_virtual_host' ? 'from-amber-400 to-orange-500' :
              plan.name === 'premium_virtual_host' ? 'from-purple-400 to-purple-600' :
              'from-purple-400 to-purple-600',
    buttonText: plan.isCustomPricing ? 'Contact Sales' :
                isUpgradeMode ? `Upgrade to ${plan.displayName}` :
                plan.name === 'yummy' ? 'Start with Yummy' :
                plan.name === 'delicious' ? 'Choose Delicious' :
                plan.name === 'gourmet' ? 'Go Gourmet' :
                plan.name === 'starter_virtual_host' ? 'Get Started' :
                plan.name === 'basic_virtual_host' ? 'Get Started' :
                plan.name === 'standard_virtual_host' ? 'Get Started' :
                plan.name === 'premium_virtual_host' ? 'Get Started' :
                'Select Plan',
    savings: plan.name === 'delicious' ? 'Most Popular' : undefined,
    // Virtual Host specific fields
    minutesIncluded: virtualHostPlanData[plan.name]?.minutes || 0,
    callsEstimate: virtualHostPlanData[plan.name]?.calls || 0,
    highlight: virtualHostPlanData[plan.name]?.highlight || '',
  }));

  const handlePlanSelect = async (planId: string) => {
    console.log('🔥 handlePlanSelect called with:', planId);
    setSelectedPlan(planId);
    setIsProcessing(true);

    try {
      // Get the actual plan data to find the database plan_id
      console.log('🔍 Looking for plan with planId:', planId);
      console.log('🔍 Available plans:', plansData?.plans);
      const selectedPlanData = plansData?.plans.find(p => p.name === planId);
      console.log('🔍 Found plan data:', selectedPlanData);
      if (!selectedPlanData) {
        throw new Error('Plan not found');
      }

      // Store selected plan info for later
      localStorage.setItem('selected_plan', planId);
      localStorage.setItem('auth_provider', authProvider);
      localStorage.setItem('billing_period', billingPeriod);
      // IMPORTANT: Also store the plan name for onboarding
      localStorage.setItem('subscription_plan', selectedPlanData.name.toLowerCase());

      // Handle UPSELL mode - adding a product to existing customer
      if (isUpsellMode && upsellRestAccountId) {
        console.log('🎯 UPSELL MODE: Creating checkout for existing customer');
        console.log('📋 Upsell details:', {
          restAccountId: upsellRestAccountId,
          productType: upsellProduct,
          planName: planId,
          billingCycle: billingPeriod === 'annual' ? 'annual' : 'monthly'
        });

        toast({
          title: "Creating checkout...",
          description: `Setting up ${selectedPlanData.displayName} plan for ${upsellRestaurantName || 'customer'}`,
        });

        const response = await salesApi.createUpsellCheckout(
          parseInt(upsellRestAccountId),
          upsellProduct as 'virtual_assistant' | 'virtual_host',
          planId,
          billingPeriod === 'annual' ? 'annual' : 'monthly'
        );

        if (response.success && response.checkout_url) {
          // Clean up localStorage
          localStorage.removeItem('upsell_rest_account_id');
          localStorage.removeItem('upsell_product_type');
          localStorage.removeItem('upsell_restaurant_name');

          // Redirect to Stripe checkout
          window.location.href = response.checkout_url;
        } else {
          throw new Error('Failed to create upsell checkout');
        }
        return;
      }

      if (isUpgradeMode) {
        // For upgrades, create upgrade checkout session
        toast({
          title: "Creating upgrade session...",
          description: "You'll be redirected to secure checkout.",
        });

        console.log('🎯 Creating upgrade session with:', {
          planId: selectedPlanData.id,
          planName: selectedPlanData.name,
          billingPeriod
        });
        
        const response = await stripeApi.createUpgradeSession(
          selectedPlanData.id,
          billingPeriod
        );

        // Handle both direct upgrade and checkout session responses
        if (response.success && response.redirect_url) {
          // Direct upgrade succeeded - redirect to success page
          toast({
            title: "Upgrade successful!",
            description: "Your subscription has been upgraded. Redirecting...",
          });
          window.location.href = response.redirect_url;
        } else if (response.checkout_url) {
          // Need checkout session for complex changes - redirect to Stripe
          const reason = response.reason;
          toast({
            title: "Redirecting to checkout...",
            description: reason === 'billing_interval_change'
              ? "Billing period change requires review. Redirecting to Stripe checkout..."
              : reason === 'plan_downgrade'
              ? "Plan downgrade requires proration review. Redirecting to Stripe checkout..."
              : "Redirecting to secure checkout to complete your subscription change.",
          });
          window.location.href = response.checkout_url;
        } else {
          throw new Error('Invalid response from upgrade endpoint');
        }
      } else {
        // For new subscriptions
        const planDetails = plansData?.plans.find(p => p.plan_id === selectedPlanData.id);
        const hasFreeTrial = planDetails?.plan_name?.toLowerCase() === 'delicious';

        // Check if this is part of FRICTIONLESS onboarding flow (need to collect restaurant details first)
        // SALES-ASSISTED flow (sessionToken) skips restaurant-setup and goes to Stripe
        if (isOnboarding && !sessionToken) {
          console.log('📝 Frictionless onboarding flow - navigating to restaurant-setup');
          toast({
            title: "Plan selected!",
            description: "Let's set up your restaurant details next.",
          });

          // Navigate to restaurant-setup with plan selection and extraction data
          navigate('/restaurant-setup', {
            state: {
              selectedPlan: planId,
              hasExtractedMenu: hasExtractedMenu,
              itemsExtracted: itemsExtracted,
              fromOAuth: fromOAuth,
              prefillData: prefillData
            }
          });
        } else {
          // User already has restaurant OR sales-assisted activation, go directly to Stripe
          if (sessionToken) {
            console.log('🎬 SALES-ASSISTED ACTIVATION: Going to Stripe checkout');
            console.log('🔑 Using activation session_token:', sessionToken);
          }

          toast({
            title: "Redirecting to checkout...",
            description: hasFreeTrial
              ? "You'll be redirected to secure checkout for your 30-day free trial."
              : "You'll be redirected to secure checkout to complete your subscription.",
          });

          // Create Stripe checkout session
          console.log('📤 [PlanSelection] Creating checkout session with plan_id:', selectedPlanData.id);
          console.log('📤 [PlanSelection] Plan name:', selectedPlanData.name);
          console.log('📤 [PlanSelection] Storing subscription_plan in localStorage:', selectedPlanData.name.toLowerCase());
          console.log('📤 [PlanSelection] Is frictionless flow?', isFrictionless);
          console.log('📤 [PlanSelection] Session token being sent to API:', {
            sessionToken: sessionToken,
            value: sessionToken || undefined,
            type: typeof sessionToken
          });

          // Use signup endpoint for frictionless flow (no auth), regular for authenticated users
          // Pass session_token if this is an activation flow (sales portal)

          // Read consent flags from localStorage (set in ActivationLanding)
          const consentData = sessionToken ? {
            terms_accepted: localStorage.getItem('activation_terms_accepted') === 'true',
            privacy_accepted: localStorage.getItem('activation_privacy_accepted') === 'true',
            email_opt_in: localStorage.getItem('activation_email_opt_in') === 'true'
          } : undefined;

          console.log('📋 [PlanSelection] Consent data:', consentData);

          const { checkout_url } = isFrictionless
            ? await stripeApi.createCheckoutSessionSignup(
                selectedPlanData.id, // Use the database plan_id
                billingPeriod,
                sessionToken || undefined, // Pass session_token for activation flow
                consentData // Pass consent flags for activation flow
              )
            : await stripeApi.createCheckoutSession(
                selectedPlanData.id, // Use the database plan_id
                billingPeriod,
                sessionToken || undefined, // Pass session_token for activation flow
                consentData // Pass consent flags for activation flow
              );

          console.log('✅ [PlanSelection] Checkout session created, redirecting to Stripe');

          // Redirect to Stripe Checkout
          window.location.href = checkout_url;
        }
      }
      
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error",
        description: "Unable to start checkout. Please try again.",
        variant: "destructive"
      });
      setIsProcessing(false);
    }
  };

  const handleContinue = async () => {
    if (!selectedPlan) {
      toast({
        title: "Please select a plan",
        description: "You need to select a plan before continuing.",
        variant: "destructive"
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Validate plan and onboarding data before payment
      console.log('Validating plan selection:', selectedPlan);
      
      const response = await fetch(getApiUrl('restaurants/v2/prepare-for-payment'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${Cookies.get('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_name: selectedPlan,
          billing_cycle: billingPeriod
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to validate plan selection');
      }
      
      const result = await response.json();
      console.log('Restaurant created successfully:', result);
      
      // Store restaurant ID for later use
      if (result.restaurant_id) {
        localStorage.setItem('restaurant_id', result.restaurant_id);
      }
      
      toast({
        title: "Success!",
        description: "Restaurant created! Proceeding to payment...",
      });
      
      // All users go through real payment flow
      // Include frictionless context if applicable
      let paymentUrl = `/payment-setup?plan=${selectedPlan}&provider=${authProvider}`;
      if (isFrictionless) {
        paymentUrl += `&frictionless=true&items=${itemsExtracted}`;
      }
      navigate(paymentUrl);
      
    } catch (error) {
      console.error('Error creating restaurant:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create restaurant. Please try again.",
        variant: "destructive"
      });
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-white text-xl">Loading plans...</div>
      </div>
    );
  }

  if (error || !plansData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-white text-xl">Failed to load plans. Please refresh the page.</div>
      </div>
    );
  }

  // Check if there are no upgrade options (already on highest plan)
  if (isUpgradeMode && plans.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] flex items-center justify-center">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo className="text-white h-10" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">You're Already on Our Best Plan!</h1>
          <p className="text-white/80 mb-6">The Gourmet plan includes all our premium features.</p>
          <Button
            onClick={() => navigate('/restaurant-dashboard')}
            className="bg-yellow-400 hover:bg-yellow-300 text-gray-900"
          >
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex justify-start mb-6 px-4">
            <Logo className="text-white h-10" />
          </div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            {isUpgradeMode ? 'Upgrade Your Plan' : isUpsellMode ? 'Add AI Virtual Host' : `Welcome ${userName}!`} {!isUpgradeMode && !isUpsellMode && '👋'}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl text-white/80 mb-8 max-w-2xl mx-auto"
          >
            {isUpgradeMode
              ? `You're currently on the ${currentSubscription?.plan_name || ''} plan. Unlock more features with an upgrade.`
              : isUpsellMode
                ? `Choose the perfect Virtual Host plan for ${upsellRestaurantName || 'your restaurant'}.`
                : 'Choose the perfect plan for your restaurant.'
            }
          </motion.p>

          {/* Frictionless Success Banner */}
          {isFrictionless && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-green-500/20 border border-green-400/30 rounded-xl p-4 mb-8 max-w-2xl mx-auto"
            >
              <div className="flex items-center justify-center gap-3">
                <Check className="w-6 h-6 text-green-400" />
                <div className="text-center">
                  <p className="text-green-400 font-semibold">
                    Great news! We've already extracted {itemsExtracted} items from your menu
                  </p>
                  <p className="text-white/70 text-sm mt-1">
                    Complete your subscription to customize and publish your menu
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Billing Period Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="flex justify-center items-center mb-8"
        >
          <div className="bg-white/10 backdrop-blur-md rounded-full p-1 flex items-center relative">
            {/* Only show monthly option for new subscriptions, not upgrades */}
            {!isUpgradeMode && (
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-blue-600'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                Monthly
              </button>
            )}
            {/* Hide quarterly for Virtual Host - they only have monthly and annual */}
            {productType !== 'virtual_host' && (
              <button
                onClick={() => setBillingPeriod('quarterly')}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 relative ${
                  billingPeriod === 'quarterly'
                    ? 'bg-white text-blue-600'
                    : 'text-white/80 hover:text-white'
                }`}
              >
                Quarterly
                {billingPeriod === 'quarterly' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: -24 }}
                    className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap"
                  >
                    Save 10%
                  </motion.div>
                )}
              </button>
            )}
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 relative ${
                billingPeriod === 'annual'
                  ? 'bg-white text-blue-600'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              {/* Save badge - always visible, yellow like VirtualHost.tsx */}
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-dineai-yellow text-yellow-900 text-xs px-2 py-0.5 rounded-full font-bold whitespace-nowrap">
                Save {productType === 'virtual_host' ? '10%' : '20%'}
              </div>
              Annual
            </button>
          </div>
        </motion.div>

        {/* Upgrade Commitment Message */}
        {isUpgradeMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-center mb-6"
          >
            <p className="text-white/80 text-sm">
              💡 <span className="font-semibold">Upgrade Benefit:</span> Choose quarterly or annual billing to maximize your savings and show your commitment to growth!
            </p>
          </motion.div>
        )}

        {/* Pricing Cards */}
        <div
          id="plan-selection"
          className={isUpgradeMode || productType === 'virtual_host'
          ? "space-y-4 mb-12 max-w-5xl mx-auto"
          : "grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-6xl mx-auto"
        }>
          {plans.map((plan, index) => {
            // Price calculation logic
            const planData = plansData.plans.find(p => p.name === plan.name);
            const isCustom = planData?.isCustomPricing;

            const getPriceDisplay = () => {
              if (isCustom) {
                return { isCustom: true, price: 'Custom', savings: null, originalPrice: null };
              }
              if (billingPeriod === 'annual' && plansData) {
                // For annual billing, show the actual annual price (not monthly equivalent)
                const annualTotal = planData?.pricing.annual.price || 0;
                const monthlyPrice = planData?.pricing.monthly.price || plan.price;
                const monthlyTotal = monthlyPrice * 12;
                const savings = (monthlyTotal - annualTotal).toFixed(0);

                return {
                  price: annualTotal,
                  savings,
                  originalPrice: monthlyTotal,
                  annualTotal,
                  isAnnual: true
                };
              } else if (billingPeriod === 'quarterly' && plansData) {
                const quarterlyPrice = planData?.pricing.quarterly.monthlyEquivalent || plan.price * 0.9;
                const savings = ((plan.price - quarterlyPrice) * 3).toFixed(0);
                const totalQuarterly = planData?.pricing.quarterly.price || (plan.price * 3 * 0.9).toFixed(2);
                return { price: quarterlyPrice.toFixed(2), savings, originalPrice: plan.price, quarterlyTotal: totalQuarterly };
              }
              return { price: plan.price, savings: null, originalPrice: plan.price };
            };

            const priceDisplay = getPriceDisplay();
            const isVirtualHost = productType === 'virtual_host';

            // Virtual Host uses the same layout as VirtualHost.tsx product page
            if (isVirtualHost) {
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className={`relative group cursor-pointer rounded-2xl p-6 border transition-all duration-300 ${
                    plan.popular
                      ? "bg-gradient-to-r from-dineai-yellow/20 to-yellow-500/10 border-dineai-yellow shadow-lg shadow-yellow-500/20"
                      : "bg-white/10 backdrop-blur-md border-white/20 hover:border-white/40 hover:bg-white/15"
                  }`}
                >
                  {/* Most Popular Badge */}
                  {plan.popular && (
                    <div className="absolute -top-3 left-6">
                      <div className="bg-dineai-yellow text-yellow-900 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                        <Star className="w-3 h-3" />
                        Most Popular
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Left: Icon + Plan Name + Description */}
                    <div className="flex items-center gap-4 md:w-1/4">
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${plan.gradient} shadow-lg transition-all duration-300 group-hover:scale-110`}>
                        <plan.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{plan.displayName}</h3>
                        <p className="text-white/60 text-sm">{plan.description}</p>
                      </div>
                    </div>

                    {/* Center: Price */}
                    <div className="md:w-1/5 md:text-center">
                      <div className="flex items-baseline gap-1 md:justify-center">
                        <span className="text-3xl font-bold text-white">
                          ${billingPeriod === "annual"
                            ? Math.round((virtualHostPlanData[plan.name]?.annualPrice || plan.price * 12) / 12)
                            : plan.price}
                        </span>
                        <span className="text-white/60 text-sm">/mo</span>
                      </div>
                      {billingPeriod === "annual" && (
                        <p className="text-dineai-yellow text-xs font-medium mt-1">
                          Billed ${(virtualHostPlanData[plan.name]?.annualPrice || 0).toLocaleString()} annually
                        </p>
                      )}
                    </div>

                    {/* Center-Right: Minutes + Calls */}
                    <div className="flex gap-8 md:w-1/4 md:justify-center">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">{plan.minutesIncluded?.toLocaleString()}</p>
                        <p className="text-white/50 text-xs">min</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-white">~{plan.callsEstimate}</p>
                        <p className="text-white/50 text-xs">calls</p>
                      </div>
                    </div>

                    {/* Right: Highlight + Button */}
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:w-1/4 md:justify-end">
                      <span className="text-white/70 text-xs">
                        {plan.highlight}
                      </span>
                      <Button
                        onClick={() => handlePlanSelect(plan.id)}
                        disabled={isProcessing}
                        className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                          selectedPlan === plan.id && isProcessing
                            ? 'bg-yellow-400 hover:bg-yellow-400 text-black cursor-wait'
                            : plan.popular
                              ? "bg-dineai-yellow text-blue-900 hover:bg-yellow-300 shadow-lg hover:shadow-xl"
                              : "bg-white/20 text-white hover:bg-white/30 border border-white/30"
                        }`}
                      >
                        {isProcessing && selectedPlan === plan.id ? (
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            Processing...
                          </div>
                        ) : (
                          <>
                            Get Started
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            } else if (isUpgradeMode) {
              // Upgrade Mode - Horizontal card layout (keep existing)
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className={`relative bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6 hover:bg-white/15 transition-all duration-300 ${
                    plan.popular ? 'ring-2 ring-yellow-400' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <div className="bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        Most Popular
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-6">
                    {/* Icon */}
                    <div className={`w-16 h-16 flex-shrink-0 rounded-2xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center`}>
                      <plan.icon className="w-8 h-8 text-white" />
                    </div>

                    {/* Content */}
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-white mb-1">{plan.displayName}</h3>
                      <p className="text-white/60 text-sm mb-3">{plan.description}</p>

                      {/* Compact features list */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {plan.features.slice(0, 4).map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                            <span className="text-white/80 text-xs">{feature}</span>
                          </div>
                        ))}
                      </div>
                      {plan.features.length > 4 && (
                        <p className="text-white/60 text-xs mt-2">+{plan.features.length - 4} more features</p>
                      )}
                    </div>

                    {/* Price and Button */}
                    <div className="flex-shrink-0 text-right">
                      <div className="mb-4">
                        {priceDisplay.isCustom ? (
                          <>
                            <span className="text-3xl font-bold text-white">Custom</span>
                            <div className="text-sm text-white/60 mt-2">
                              Contact us for pricing
                            </div>
                          </>
                        ) : (
                          <>
                            <span className="text-3xl font-bold text-white">${priceDisplay.price}</span>
                            <span className="text-white/60">/mo</span>
                            {priceDisplay.savings && (
                              <div className="text-sm text-white/60 mt-1">
                                <span className="line-through">${priceDisplay.originalPrice}/mo</span>
                                <span className={`ml-2 ${billingPeriod === 'annual' ? 'text-green-400' : 'text-orange-400'}`}>
                                  Save ${priceDisplay.savings}
                                </span>
                              </div>
                            )}
                            {priceDisplay.annualTotal && (
                              <div className="text-xs text-white/50 mt-1">
                                Billed ${priceDisplay.annualTotal} annually
                              </div>
                            )}
                            {priceDisplay.quarterlyTotal && (
                              <div className="text-xs text-white/50 mt-1">
                                Billed ${priceDisplay.quarterlyTotal} quarterly
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      <Button
                        onClick={() => {
                          if (isCustom) {
                            window.location.href = 'https://dineai.ai/contact#contact-form';
                          } else {
                            handlePlanSelect(plan.id);
                          }
                        }}
                        disabled={isProcessing}
                        className={`whitespace-nowrap px-6 py-3 text-sm font-semibold rounded-lg transition-all duration-300 ${
                          selectedPlan === plan.id && isProcessing
                            ? 'bg-yellow-400 hover:bg-yellow-400 text-black cursor-wait'
                            : plan.popular
                              ? 'bg-yellow-400 hover:bg-yellow-300 text-black'
                              : 'bg-white/20 hover:bg-yellow-400 hover:text-black text-white border border-white/30'
                        }`}
                      >
                        {isProcessing && selectedPlan === plan.id ? (
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            Processing...
                          </div>
                        ) : (
                          isCustom ? 'Contact Sales' : `Upgrade to ${plan.displayName}`
                        )}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            } else {
              // Normal Mode - Vertical card layout
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className={`relative bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6 hover:bg-white/15 transition-all duration-300 flex flex-col ${
                    plan.popular ? 'ring-2 ring-yellow-400 scale-105' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <div className="bg-yellow-400 text-yellow-900 px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                        <Star className="w-4 h-4" />
                        Most Popular
                      </div>
                    </div>
                  )}

                  {/* Icon and Title */}
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center`}>
                      <plan.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{plan.displayName}</h3>
                    <p className="text-white/60 text-sm">{plan.description}</p>
                  </div>

                  {/* Price */}
                  <div className="text-center mb-6">
                    {priceDisplay.isCustom ? (
                      <>
                        <span className="text-4xl font-bold text-white">Custom</span>
                        <div className="text-sm text-white/60 mt-2">
                          Contact us for pricing
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <span className="text-4xl font-bold text-white">${priceDisplay.price}</span>
                          <span className="text-white/60">/{priceDisplay.isAnnual ? 'year' : 'month'}</span>
                        </div>
                        {priceDisplay.savings && (
                          <div className="text-sm text-white/60 mt-2">
                            <span className="line-through">${priceDisplay.originalPrice}{priceDisplay.isAnnual ? '/year' : '/mo'}</span>
                            <span className={`ml-2 ${billingPeriod === 'annual' ? 'text-green-400' : 'text-orange-400'}`}>
                              Save ${priceDisplay.savings}/{billingPeriod === 'annual' ? 'year' : 'quarter'}
                            </span>
                          </div>
                        )}
                        {priceDisplay.annualTotal && !priceDisplay.isAnnual && (
                          <div className="text-xs text-white/50 mt-1">
                            Billed ${priceDisplay.annualTotal} annually
                          </div>
                        )}
                        {priceDisplay.quarterlyTotal && (
                          <div className="text-xs text-white/50 mt-1">
                            Billed ${priceDisplay.quarterlyTotal} every 3 months
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6 flex-grow">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span className="text-white/80 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Button */}
                  <Button
                    onClick={() => {
                      if (isCustom) {
                        // Navigate to Contact page with form section
                        window.location.href = 'https://dineai.ai/contact#contact-form';
                      } else {
                        handlePlanSelect(plan.id);
                      }
                    }}
                    disabled={isProcessing}
                    className={`w-full py-6 text-lg font-semibold rounded-xl transition-all duration-300 ${
                      selectedPlan === plan.id && isProcessing
                        ? 'bg-yellow-400 hover:bg-yellow-400 text-black shadow-lg cursor-wait'
                        : plan.popular
                          ? 'bg-yellow-400 hover:bg-yellow-300 text-black shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                          : 'bg-white/20 hover:bg-yellow-400 hover:text-black text-white border border-white/30 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                    }`}
                  >
                    {isProcessing && selectedPlan === plan.id ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        {isCustom ? (
                          <>
                            Contact Sales
                            <ArrowRight className="w-5 h-5" />
                          </>
                        ) : plan.id === 'yummy' ? (
                          <>
                            Buy Now
                            <ArrowRight className="w-5 h-5" />
                          </>
                        ) : plan.id === 'delicious' ? (
                          <>
                            Start 30-Day Free Trial
                            <ArrowRight className="w-5 h-5" />
                          </>
                        ) : plan.id === 'gourmet' ? (
                          <>
                            Go Gourmet
                            <ArrowRight className="w-5 h-5" />
                          </>
                        ) : (
                          <>
                            {plan.buttonText}
                            <ArrowRight className="w-5 h-5" />
                          </>
                        )}
                      </div>
                    )}
                  </Button>
                </motion.div>
              );
            }
          })}
        </div>

        {/* Virtual Host Usage Fees Section */}
        {productType === 'virtual_host' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-8 text-center max-w-5xl mx-auto"
          >
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-white/60 text-sm mb-3">
              <span>Overage: <span className="text-white/90 font-medium">10¢/min</span></span>
              <span>Convenience Fee: <span className="text-white/90 font-medium">99¢ - $1.99/order</span></span>
              <span>Setup: <span className="text-white/90 font-medium">$500 one-time</span></span>
            </div>
            <p className="text-white/40 text-xs">
              Convenience fee (99¢ under $30, $1.49 for $30-75, $1.99 over $75) charged to customer. Stripe processing (2.9% + 30¢) applies.
            </p>
          </motion.div>
        )}

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="text-center mt-12"
        >
          <p className="text-white/60 text-sm mb-4">The future of restaurant technology</p>
          <div className="flex justify-center items-center gap-8 text-white/40">
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              <span className="text-xs">Secure</span>
            </div>
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4" />
              <span className="text-xs">5-Star Support</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PlanSelection;